<template>
  <div>
    <div class="tw:md:hidden tw:w-full tw:border-b tw:border-blue-300">
      <button
        class="tw:flex tw:justify-between tw:text-blue-800 tw:w-full tw:text-left tw:focus:outline-0 tw:py-4 tw:px-4 tw:relative"
        @click="mobileDropdownOpen = !mobileDropdownOpen"
        @keydown.enter="mobileDropdownOpen = !mobileDropdownOpen"
      >
        {{ $t(termsAppList[selectedOptionInd].label) }}
        <ChevronRight class="tw:text-black tw:size-6" :class="mobileDropdownOpen ? 'tw:-rotate-90' : 'tw:rotate-90'" />
      </button>
      <ul :class="mobileDropdownOpen ? 'tw:block' : 'tw:hidden'">
        <li v-for="(appName, i) in termsAppList" :key="i" class="tw:text-black">
          <button
            v-if="i !== selectedOptionInd"
            class="tw:focus:outline-0 tw:text-left tw:w-full tw:pb-4 tw:px-4"
            @click="handleOptionClick(i)"
            @keydown.enter="handleOptionClick(i)"
          >
            {{ $t(appName.label) }}
          </button>
        </li>
      </ul>
    </div>
    <ul
      class="tw:hidden tw:md:flex tw:justify-between tw:gap-2 tw:max-w-(--tw:breakpoint-xl) tw:mx-auto tw:border-b tw:border-gray-300 tw:pb-4 tw:px-4 tw:my-5"
    >
      <li
        v-for="(appName, i) in termsAppList"
        :key="i"
        :class="i === selectedOptionInd ? 'tw:text-blue-800' : 'tw:text-black'"
      >
        <button class="tw:focus:outline-0" @click="handleOptionClick(i)" @keydown.enter="handleOptionClick(i)">
          {{ $t(appName.label) }}
        </button>
      </li>
    </ul>
    <section
      id="terms-content"
      data-testid="terms-content"
      class="tw:max-w-4xl tw:py-8 tw:px-4 tw:text-justify tw:mx-auto"
    >
      <h1 class="tw:text-3xl tw:md:text-5xl tw:font-bold tw:text-center tw:mb-4">
        {{ $t(termsAppList[selectedOptionInd].label) }}
      </h1>
      <div class="tw:border-b tw:border-gray-300 tw:pb-5 tw:mb-8 tw:text-xl tw:text-center">
        <h2 class="tw:mb-4 tw:text-xl tw:text-center" v-html="$t('terms_and_conditions.heading')"></h2>
        <small class="tw:text-gray-600 tw:font-light" v-if="termsAppList[selectedOptionInd]?.date">
          {{ $t(termsAppList[selectedOptionInd].date) }}
        </small>
      </div>
      <div class="terms-body" v-html="$t(termsAppList[selectedOptionInd].terms)"></div>
    </section>
    <div class="tw:flex tw:justify-center tw:mb-8">
      <MainLinkButton
        :href="pdfUrl"
        :download="pdfFilename"
        data-testid="tc-download-button"
        class="tw:inline-flex tw:items-center tw:gap-2"
      >
        <DownloadIcon />
        {{ $t('rc_data_download_cta') }}
      </MainLinkButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'

import ChevronRight from '@/components/icons/ChevronRight.svg?component'
import MainLinkButton from '@/components/base/buttons/MainLinkButton.vue'
import DownloadIcon from '@/components/icons/DownloadIcon.svg?component'
import { useSessionValues } from '@/composables/useSessionValues'

interface TermsApp {
  label: string
  terms: string
  date?: string
  filePrefix: string
}

const route = useRoute()
const selectedOptionInd = ref<number>(0)
const mobileDropdownOpen = ref<boolean>(false)

const { language } = useSessionValues()

const termsAppList: TermsApp[] = [
  { label: 'appSwitcher.dashboardTitle', terms: 'terms_and_conditions.terms', filePrefix: 'TermsAndConditions' },
  {
    label: 'app.rateConnect.name',
    terms: 'rc.terms_and_conditions.terms',
    date: 'rc_terms_and_conditions_date',
    filePrefix: 'RateConnectTerms',
  },
  {
    label: 'studio.plus.compare.features.businessstudioplus.title',
    terms: 'business_studio_plus_terms_and_conditions.terms',
    date: 'plus_terms_and_conditions_date',
    filePrefix: 'StudioPlusTerms',
  },
]

const pdfUrl = computed<string>(() => {
  const { filePrefix } = termsAppList[selectedOptionInd.value]
  return `/home/tc/${filePrefix}_${language.value}.pdf`
})

const pdfFilename = computed<string>(() => {
  const { filePrefix } = termsAppList[selectedOptionInd.value]
  return `${filePrefix}_${language.value}.pdf`
})

onMounted(() => {
  if (route.query['app'] === 'rate-connect') {
    selectedOptionInd.value = 1
  } else if (route.query['app'] === 'business-studio-plus') {
    selectedOptionInd.value = 2
  }
})

const handleOptionClick = (optionIndex: number): void => {
  mobileDropdownOpen.value = false
  selectedOptionInd.value = optionIndex
}
</script>

<style scoped>
.terms-body :deep(p) {
  margin-bottom: 16px;
}
.terms-body :deep(a) {
  color: #007bff;
}
.terms-body :deep(h3) {
  font-weight: bold;
  margin-bottom: 16px;
  font-size: 28px;
  line-height: 35px;
  margin-top: 32px;
}
.terms-body :deep(#exhibit-1) {
  padding-top: 64px;
}

.terms-body :deep(#exhibit-2) {
  padding-top: 64px;
}
</style>
